 import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { isSSRRendered, logSSRDetection } from "@/utils/ssrDetection";
import { createOrganizationSchema, createWebsiteSchema, combineSchemas } from "@/utils/schemaUtils";
import { useNavigationSEO } from "@/hooks/useNavigationSEO";
import { optimizeMetaTags } from "@/utils/seoUtils";
import { debugLog } from "@/utils/debugLogger";
import { waitForHydrationComplete } from "@/utils/hydrationManager";

interface StructuredData {
  '@context': string;
  '@type': string;
  [key: string]: any;
}

interface PageSEOProps {
  title: string;
  description: string;
  canonicalPath: string;
  ogImage?: string;
  structuredData?: StructuredData | StructuredData[];
  noIndex?: boolean;
  ssrMode?: boolean;
  children?: React.ReactNode;
  /** @deprecated keywords meta tag removed - Google ignores it since 2009 */
  keywords?: string[];
  ogType?: string;
  language?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  breadcrumbs?: { name: string; url: string }[];
  articleSection?: string;
  articleTags?: string[];
  readingTime?: string;
  wordCount?: number;
  geoTargeting?: {
    country?: string;
    region?: string;
    placename?: string;
    position?: string;
  };
  resourceHints?: {
    preconnect?: string[];
    dnsPrefetch?: string[];
    preload?: Array<{ href: string; as: string; type?: string }>;
  };
}

export const PageSEO = ({
  title,
  description,
  canonicalPath,
  ogImage,
  structuredData,
  noIndex = false,
  ssrMode = false,
  children,
  keywords,
  ogType = "website",
  language = "it-IT",
  author,
  publishedTime,
  modifiedTime,
  breadcrumbs,
  articleSection,
  articleTags,
  readingTime,
  wordCount,
  geoTargeting,
  resourceHints
}: PageSEOProps) => {
  // Trigger cleanup of duplicate meta tags
  useNavigationSEO();
  
  // Hydration guard: skip JSON-LD during initial hydration to prevent mismatch
  // JSON-LD is already rendered by SSR in entry-server-conditional.tsx
  const [isHydrated, setIsHydrated] = useState(false);
  
  // Wait for global hydration signal instead of arbitrary timeout
  useEffect(() => {
    let cancelled = false;
    
    waitForHydrationComplete().then(() => {
      if (!cancelled) setIsHydrated(true);
    });
    
    return () => { cancelled = true; };
  }, []);
  
  // Log SEO rendering in debug mode
  if (typeof window !== 'undefined') {
    debugLog('ssr', `Rendering SEO for ${window.location.pathname}: ${title}`, undefined, 'PageSEO');
  }

  // Update DOM meta tags on client-side for navigation
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const canonicalUrl = `https://www.gildy.it${canonicalPath?.startsWith('/') ? '' : '/'}${canonicalPath || ''}`;
      const imageUrl = ogImage && ogImage !== 'undefined' && !ogImage.includes('placeholder.svg') 
        ? ogImage 
        : "https://www.gildy.it/logo.png";

      optimizeMetaTags({
        title,
        description,
        canonicalUrl,
        ogTitle: title,
        ogDescription: description,
        ogImage: imageUrl,
      });
    }
  }, [title, description, canonicalPath, ogImage]);

  // Always render SEO - no blocking logic whatsoever
  
  // Build full canonical URL with proper validation
  const cleanCanonicalPath = canonicalPath && canonicalPath !== 'undefined' && canonicalPath !== '' 
    ? canonicalPath 
    : '/';
  
  const canonicalUrl = `https://www.gildy.it${cleanCanonicalPath.startsWith('/') ? '' : '/'}${cleanCanonicalPath}`;
  
  // Default image URL if not specified - also validate against undefined/placeholder
  const cleanOgImage = ogImage && ogImage !== 'undefined' && !ogImage.includes('placeholder.svg') 
    ? ogImage 
    : "https://www.gildy.it/logo.png";
  const imageUrl = cleanOgImage;
  
  // Prepare structured data - Organization/Website schemas are generated by SSR
  // Only use provided schemas to avoid duplication (see docs/faq-schema-ssr.md)
  let jsonLdData: string = '';
  
  if (structuredData) {
    if (Array.isArray(structuredData)) {
      jsonLdData = combineSchemas(...structuredData);
    } else {
      jsonLdData = JSON.stringify(structuredData);
    }
  }
  // No default schemas - SSR handles Organization/Website in entry-server-conditional.tsx
  
  // Return the Helmet content directly since HelmetProvider is already provided in entry-server.tsx
  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Resource Hints for Performance */}
      {resourceHints?.preconnect?.map((url, index) => (
        <link key={`preconnect-${index}`} rel="preconnect" href={url} />
      ))}
      {resourceHints?.dnsPrefetch?.map((url, index) => (
        <link key={`dns-prefetch-${index}`} rel="dns-prefetch" href={url} />
      ))}
      {resourceHints?.preload?.map((resource, index) => (
        <link 
          key={`preload-${index}`} 
          rel="preload" 
          href={resource.href} 
          as={resource.as}
          type={resource.type}
        />
      ))}
      
      {/* Geo-targeting Meta Tags */}
      {geoTargeting?.country && (
        <meta name="geo.region" content={geoTargeting.country} />
      )}
      {geoTargeting?.placename && (
        <meta name="geo.placename" content={geoTargeting.placename} />
      )}
      {geoTargeting?.position && (
        <meta name="geo.position" content={geoTargeting.position} />
      )}
      {geoTargeting?.region && (
        <meta name="ICBM" content={geoTargeting.region} />
      )}
      
      {/* Author */}
      {author && <meta name="author" content={author} />}
      
      {/* Reading time metadata */}
      {readingTime && <meta name="reading-time" content={readingTime} />}
      {wordCount && <meta name="word-count" content={String(wordCount)} />}
      
      {/* Mobile App Meta Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Gildy" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Gildy" />
      <meta property="og:locale" content={language} />
      
      {/* Enhanced Open Graph tags for blog articles */}
      {ogType === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {ogType === 'article' && author && (
        <meta property="article:author" content={author} />
      )}
      {ogType === 'article' && articleSection && (
        <meta property="article:section" content={articleSection} />
      )}
      {ogType === 'article' && articleTags && articleTags.length > 0 && articleTags.map((tag, index) => (
        <meta key={`tag-${index}`} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@gildy_it" />
      
      {/* Twitter author */}
      {author && <meta name="twitter:creator" content={`@${author.replace(/\s/g, '').toLowerCase()}`} />}
      
      {/* Reading time for social platforms */}
      {readingTime && <meta name="twitter:label1" content="Tempo di lettura" />}
      {readingTime && <meta name="twitter:data1" content={readingTime} />}
      
      {/* Robots tag removed - handled server-side in entry-server-conditional.tsx */}
      
      {/* Performance Hints */}
      <meta name="referrer" content="origin-when-cross-origin" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="color-scheme" content="light dark" />
      
      {/* Structured data (JSON-LD) - only after hydration to avoid SSR mismatch */}
      {isHydrated && jsonLdData && (
        <script type="application/ld+json" suppressHydrationWarning>
          {jsonLdData}
        </script>
      )}
      
      {/* Additional SEO meta tags */}
      <meta name="fragment" content="!" />
      <meta name="prerender" content="active" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {children}
    </Helmet>
  );
};
